import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "utils/api";

const ServicesContext = createContext(null);

export const useServices = () => {
  const ctx = useContext(ServicesContext);
  if (!ctx) throw new Error("useServices must be used within <ServicesProvider>");
  return ctx;
};

// ---- Helpers to normalize backend → UI
const normalizeService = (row) => ({
  id: row.id,
  name: row.name,
  banner_img: row.banner_img || "",
  status: row.status === 1 || row.status === "1" || row.status === "active" ? "active" : "inactive",
  products: Array.isArray(row.products) ? row.products : [],
  addons: Array.isArray(row.addons) ? row.addons : [],
});

const normalizeAddon = (row) => ({
  id: row.id,
  name: row.name,
  fee: row.fee,
  image: row.banner_img || "",
  status: row.status === 1 || row.status === "1" ? "active" : "inactive",
});

const normalizeProduct = (row) => ({
  id: row.id,
  name: row.product_name,
  fee: row.product_fee,
  image: row.banner_img || "",
  status: row.status === 1 || row.status === "1" ? "active" : "inactive",
});

export const ServicesProvider = ({ children }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Fetch services + their products + addons
  const fetchServices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setServices([]);
        return;
      }

      const svcRes = await api.get("/list_service");
      const svcRows = Array.isArray(svcRes.data) ? svcRes.data : [];

      const populated = await Promise.all(
        svcRows.map(async (s) => {
          let prods = [];
          let addons = [];
          try {
            const prodRes = await api.post("/list_service_product", { service_id: s.id });
            prods = Array.isArray(prodRes.data) ? prodRes.data.map(normalizeProduct) : [];
          } catch { /* ignore */ }
          try {
            const addonRes = await api.post("/list_addon_service", { service_id: s.id });
            addons = Array.isArray(addonRes.data) ? addonRes.data.map(normalizeAddon) : [];
          } catch { /* ignore */ }
          return normalizeService({ ...s, products: prods, addons });
        })
      );

      setServices(populated);
    } catch (e) {
      console.error(e);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // ---- Add Service
  const addService = async ({ name, banner_img = "" }) => {
    if (!name?.trim()) {
      toast.error("Please enter a service name");
      return null;
    }
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/add_service", { name: name.trim(), banner_img, user_id });
      await fetchServices();
      toast.success("Service created successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to create service");
      return null;
    }
  };

  // ---- Edit Service
  const editService = async ({ id, name, banner_img = "" }) => {
    if (!id || !name?.trim()) {
      toast.error("Missing service id or name");
      return null;
    }
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/edit_service", { service_id: id, name: name.trim(), banner_img, user_id });
      await fetchServices();
      toast.success("Service updated successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update service");
      return null;
    }
  };

  // ---- Toggle Service Status
  const toggleServiceStatus = async (serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    if (!svc) return;

    const nextStatus = svc.status === "active" ? "inactive" : "active";
    setServices((prev) =>
      prev.map((s) => (s.id === serviceId ? { ...s, status: nextStatus } : s))
    );

    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/update_status_service", {
        service_id: serviceId,
        status: nextStatus === "active" ? 1 : 2,
        user_id,
      });
      await fetchServices();
      toast.success(`Service ${nextStatus === "active" ? "activated" : "deactivated"}`);
    } catch (e) {
      console.error(e);
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? { ...s, status: svc.status } : s))
      );
      toast.error("Failed to update status");
    }
  };

  // ---- "Delete" Service (soft delete)
  const deleteService = async (serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    if (!svc) return;

    const count = svc.products?.length || 0;
    const ok = window.confirm(
      count > 0
        ? `Set "${svc.name}" to inactive and its ${count} product(s) will remain associated. Continue?`
        : `Set "${svc.name}" to inactive?`
    );
    if (!ok) return;

    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/update_status_service", { service_id: serviceId, status: 2, user_id });
      await fetchServices();
      toast.success(`"${svc.name}" set to inactive`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to deactivate service");
    }
  };

  // ---- Add Product
  const addProduct = async (serviceId, product) => {
    const { name, fee, image = "" } = product || {};
    if (!serviceId || !name || fee === undefined || fee === null || fee === "") {
      toast.error("Please fill in all product fields");
      return;
    }
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/add_product", {
        service_id: serviceId, product_name: name, product_fee: Number(fee), banner_img: image, user_id,
      });
      await fetchServices();
      toast.success("Product added successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add product");
    }
  };

  // ---- Edit Product
  const editProduct = async (productId, updates) => {
    const { name, fee, image = "" } = updates || {};
    if (!productId || !name || fee === undefined || fee === null || fee === "") {
      toast.error("Please provide product id, name and fee");
      return;
    }
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/edit_product", {
        product_id: productId, product_name: name, product_fee: Number(fee), banner_img: image, user_id,
      });
      await fetchServices();
      toast.success("Product updated successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update product");
    }
  };

  // ---- Remove/Deactivate Product
  const removeProduct = async (serviceId, productId) => {
    if (!window.confirm("Are you sure you want to remove this product?")) return;
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/update_product_status", { product_id: productId, status: 2, user_id });
      await fetchServices();
      toast.success("Product removed");
    } catch (e) {
      console.error(e);
      toast.error("Failed to remove product");
    }
  };

  // ---- Add Addon Service
  const addAddon = async (serviceId, addon) => {
    const { name, fee, image = "" } = addon || {};
    if (!serviceId || !name || fee === undefined || fee === null || fee === "") {
      toast.error("Please fill in all addon fields");
      return;
    }
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/add_addon_service", {
        service_id: serviceId, name, fee: Number(fee), banner_img: image, user_id,
      });
      await fetchServices();
      toast.success("Addon added successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add addon");
    }
  };

  // ---- Edit Addon Service
  const editAddon = async (addonId, updates) => {
    const { name, fee, image = "" } = updates || {};
    if (!addonId || !name || fee === undefined || fee === null || fee === "") {
      toast.error("Please provide addon id, name and fee");
      return;
    }
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/edit_addon_service", {
        addon_id: addonId, name, fee: Number(fee), banner_img: image, user_id,
      });
      await fetchServices();
      toast.success("Addon updated successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update addon");
    }
  };

  // ---- Remove/Deactivate Addon Service
  const removeAddon = async (serviceId, addonId) => {
    if (!window.confirm("Are you sure you want to remove this addon?")) return;
    const user_id = localStorage.getItem("user_id");
    try {
      await api.post("/update_addon_service_status", { addon_id: addonId, status: 2, user_id });
      await fetchServices();
      toast.success("Addon removed");
    } catch (e) {
      console.error(e);
      toast.error("Failed to remove addon");
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      services, loading, fetchServices,
      addService, editService, deleteService, toggleServiceStatus,
      addProduct, editProduct, removeProduct,
      addAddon, editAddon, removeAddon,
    }),
    [services, loading]
  );

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
};
